import { useEffect, useState } from "react";
import { AnimatePresence, motion, useDragControls } from "framer-motion";
import { Check, X } from "lucide-react";
import { waLink } from "@/siteConfig";
import { useLang } from "@/i18n";

const CODES = ["+91", "+971", "+974", "+966", "+965", "+1", "+44", "+65", "+61", "+852"];
const INTERESTS = ["fh.opt.weekend", "fh.opt.plantation", "fh.opt.river", "fh.opt.coast", "fh.opt.else"];
const BUDGETS = ["fh.b.u1", "fh.b.13", "fh.b.35", "fh.b.5", "fh.b.tbd"];
const WHEN = ["fh.w.now", "fh.w.36", "fh.w.exp"];
const EASE = [0.22, 1, 0.36, 1];

const Opt = ({ active, error, onClick, testId, children }) => (
  <button
    type="button"
    data-testid={testId}
    onClick={onClick}
    className={`min-h-[44px] px-4 border font-mono-label text-[11px] tracking-[0.12em] uppercase transition-colors duration-200 ${
      active
        ? "border-[#B8894B] bg-[#1A2A24] text-[#B8894B]"
        : error
          ? "border-[#B8894B]/60 text-[#EDE7DA]/70"
          : "border-[#1A2A24] text-[#EDE7DA]/70 active:border-[#B8894B]/60 active:text-[#EDE7DA]"
    }`}
  >
    {children}
  </button>
);

const Label = ({ error, children, t }) => (
  <span
    className={`font-mono-label text-[10px] tracking-[0.25em] uppercase transition-colors duration-300 ${
      error ? "text-[#B8894B]" : "text-[#EDE7DA]/45"
    }`}
  >
    {children}
    {error ? ` ${t("lc.required")}` : ""}
  </span>
);

export const FarmhouseSheet = ({ open, intent, onClose }) => {
  const { t, tf } = useLang();
  const controls = useDragControls();
  const [name, setName] = useState("");
  const [code, setCode] = useState("+91");
  const [phone, setPhone] = useState("");
  const [what, setWhat] = useState("");
  const [budget, setBudget] = useState("");
  const [when, setWhen] = useState("");
  const [notes, setNotes] = useState("");
  const [attempted, setAttempted] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    if (open) {
      if (sent) {
        setName("");
        setPhone("");
        setCode("+91");
        setBudget("");
        setWhen("");
        setNotes("");
      }
      setWhat(intent || "");
      setSent(false);
      setAttempted(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, intent]);

  useEffect(() => {
    if (!open) return;
    window.__lenis?.stop();
    document.body.style.overflow = "hidden";
    return () => {
      window.__lenis?.start();
      document.body.style.overflow = "";
    };
  }, [open]);

  const missing = { name: !name.trim(), phone: !phone.trim(), what: !what };
  const err = (k) => attempted && missing[k];

  const lines = [
    t("wa.farm.title"),
    "—",
    `${t("wa.name")}: ${name.trim()}`,
    `${t("wa.phone")}: ${code} ${phone.trim()}`,
    `${t("wa.looking")}: ${t(what)}`,
  ];
  if (budget) lines.push(`${t("wa.budget")}: ${t(budget)}`);
  if (when) lines.push(`${t("wa.when")}: ${t(when)}`);
  if (notes.trim()) lines.push(`${t("wa.notes")}: ${notes.trim()}`);
  const url = waLink(lines.join("\n"));

  const submit = () => {
    setAttempted(true);
    if (Object.values(missing).some(Boolean)) return;
    window.open(url, "_blank", "noopener,noreferrer");
    setSent(true);
  };

  const inputCls = (error) =>
    `h-12 w-full bg-[#0E1613] border px-4 text-base font-light outline-none transition-colors duration-300 ${
      error ? "border-[#B8894B] text-[#B8894B]" : "border-[#1A2A24] focus:border-[#B8894B] text-[#EDE7DA]"
    } placeholder-[#EDE7DA]/25`;

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            key="farm-backdrop"
            data-testid="farm-sheet-backdrop"
            className="fixed inset-0 z-[60] bg-[#0E1613]/70 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            onClick={onClose}
          />
          <motion.div
            key="farm-sheet"
            data-testid="farm-sheet"
            role="dialog"
            aria-modal="true"
            aria-label={t("fh.sheet.h")}
            className="fixed inset-x-0 bottom-0 z-[70] mx-auto w-full max-w-[480px] max-h-[86vh] overflow-y-auto bg-[#131F1A] border-t border-x border-[#1A2A24]"
            style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.5, ease: EASE }}
            drag="y"
            dragListener={false}
            dragControls={controls}
            dragConstraints={{ top: 0 }}
            dragElastic={{ top: 0, bottom: 0.4 }}
            onDragEnd={(e, info) => {
              if (info.offset.y > 110 || info.velocity.y > 600) onClose();
            }}
          >
            <div
              className="sticky top-0 z-10 bg-[#131F1A] border-b border-[#1A2A24] px-6 pt-2.5 pb-4 cursor-grab touch-none"
              onPointerDown={(e) => controls.start(e)}
            >
              <span className="mx-auto block h-1 w-10 bg-[#1A2A24]" />
              <div className="mt-3.5 flex items-start justify-between gap-4">
                <div>
                  <h2 className="font-display text-2xl tracking-tight text-[#EDE7DA]">
                    {t("fh.sheet.h")}
                  </h2>
                  <p className="mt-1.5 text-xs font-light text-[#EDE7DA]/55 leading-relaxed">
                    {t("fh.sheet.sub")}
                  </p>
                </div>
                <button
                  type="button"
                  data-testid="farm-sheet-close"
                  onClick={onClose}
                  aria-label={t("cta.close")}
                  className="shrink-0 w-11 h-11 -mr-2 flex items-center justify-center text-[#EDE7DA]/60 active:text-[#B8894B]"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {sent ? (
              <div data-testid="farm-sheet-confirmation" className="px-6 py-8">
                <span className="inline-flex items-center justify-center w-11 h-11 border border-[#B8894B]">
                  <Check className="w-5 h-5 text-[#B8894B]" />
                </span>
                <h3 className="font-display text-2xl text-[#EDE7DA] tracking-tight mt-6">
                  {tf("fh.done.h", { name: name.trim() })}
                </h3>
                <p className="font-light text-sm text-[#EDE7DA]/60 leading-relaxed mt-3">
                  {t("fh.done.body")}
                </p>
                <a
                  data-testid="farm-sheet-fallback-link"
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block mt-7 font-mono-label text-[11px] tracking-[0.2em] uppercase text-[#B8894B] border-b border-[#B8894B]/40 pb-1"
                >
                  {t("lc.done.fallback")}
                </a>
              </div>
            ) : (
              <div className="px-6 py-6 grid gap-6">
                <div>
                  <Label t={t} error={err("what")}>{t("fh.f.interest")}</Label>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="farm-sheet-interests">
                    {INTERESTS.map((k) => (
                      <Opt
                        key={k}
                        testId={`farm-interest-${k.split(".").pop()}`}
                        active={what === k}
                        error={err("what")}
                        onClick={() => setWhat(k)}
                      >
                        {t(k)}
                      </Opt>
                    ))}
                  </div>
                </div>

                <div>
                  <Label t={t} error={err("name")}>{t("bf.name")}</Label>
                  <input
                    data-testid="farm-name-input"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    placeholder={t("ph.name")}
                    autoComplete="name"
                    className={`mt-3 ${inputCls(err("name"))}`}
                  />
                </div>

                <div>
                  <Label t={t} error={err("phone")}>{t("fh.f.phone")}</Label>
                  <div className="mt-3 flex gap-2">
                    <select
                      data-testid="farm-code-select"
                      value={code}
                      onChange={(e) => setCode(e.target.value)}
                      aria-label={t("fh.f.code")}
                      className="h-12 w-[96px] shrink-0 bg-[#0E1613] border border-[#1A2A24] focus:border-[#B8894B] outline-none px-3 text-base text-[#EDE7DA]"
                    >
                      {CODES.map((c) => (
                        <option key={c} value={c}>
                          {c}
                        </option>
                      ))}
                    </select>
                    <input
                      data-testid="farm-phone-input"
                      type="tel"
                      inputMode="numeric"
                      autoComplete="tel-national"
                      value={phone}
                      onChange={(e) => setPhone(e.target.value.replace(/[^\d\s-]/g, ""))}
                      placeholder={t("fh.ph.phone")}
                      className={inputCls(err("phone"))}
                    />
                  </div>
                </div>

                <div>
                  <Label t={t}>{t("fh.f.budget")}</Label>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="farm-sheet-budgets">
                    {BUDGETS.map((k) => (
                      <Opt
                        key={k}
                        testId={`farm-budget-${k.split(".").pop()}`}
                        active={budget === k}
                        onClick={() => setBudget(budget === k ? "" : k)}
                      >
                        {t(k)}
                      </Opt>
                    ))}
                  </div>
                </div>

                <div>
                  <Label t={t}>{t("fh.f.when")}</Label>
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="farm-sheet-whens">
                    {WHEN.map((k) => (
                      <Opt
                        key={k}
                        testId={`farm-when-${k.split(".").pop()}`}
                        active={when === k}
                        onClick={() => setWhen(when === k ? "" : k)}
                      >
                        {t(k)}
                      </Opt>
                    ))}
                  </div>
                </div>

                <div>
                  <Label t={t}>{t("bf.notes")}</Label>
                  <textarea
                    data-testid="farm-notes-input"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder={t("lc.notesPh")}
                    rows={2}
                    className="mt-3 w-full resize-none bg-[#0E1613] border border-[#1A2A24] focus:border-[#B8894B] outline-none p-4 text-base text-[#EDE7DA] placeholder-[#EDE7DA]/25 transition-colors duration-300"
                  />
                </div>

                <div>
                  <button
                    type="button"
                    data-testid="farm-sheet-submit"
                    onClick={submit}
                    className="h-14 w-full bg-[#B8894B] text-[#0E1613] font-mono-label text-xs tracking-[0.2em] uppercase active:bg-[#EDE7DA] transition-colors duration-200"
                  >
                    {t("fh.f.submit")}
                  </button>
                  <p className="mt-4 text-center font-mono-label text-[10px] tracking-[0.15em] uppercase text-[#EDE7DA]/40">
                    {t("cta.waNote")}
                  </p>
                </div>
              </div>
            )}
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};
